import { CultureInfo } from "../../Dom/CultureInfo";
import { GlobalContext } from "../GlobalContext";

export class DateUtils {
    public static readonly miliSecPerDay: number = 24 * 60 * 60 * 1000;

    // Staticka promenna pro ukladani funkci
    private static readonly _dateToStringFunctionList: any = {};

    public static fromXsdDateTime(s: string): Date | null {
        if (s == null)
            return null;

        const re = /Z|[+-][0-9]{2}:[0-9]{2}$/;
        let match: any;
        let value: string;

        if (typeof s === "string") {
            match = s.match(re);
            value = s;
        } else {
            if ((s as any).v == null)
                return null;
            match = (s as any).v.match(re);
            value = (s as any).v;
        }

        if (match)
            return new Date(value);

        const d = new Date(value + "Z");
        d.setMinutes(d.getMinutes() + d.getTimezoneOffset());

        return d;
    }

    public static toXsdDateTime(d: Date, inUtc: boolean = false): string {
        if (!d || isNaN(d.getTime()))
            return "";
        if (inUtc)
            return d.toISOString();

        const e = new Date(d.getTime());
        e.setMinutes(e.getMinutes() - e.getTimezoneOffset());

        if (isNaN(e.getTime())) {
            return d.toISOString().slice(0, -1);
        }
        return e.toISOString().slice(0, -1);
    }

    public static compareDates(d1: Date, d2: Date): boolean {
        return d1.getMonth() == d2.getMonth() && d1.getDate() == d2.getDate() &&
            d1.getFullYear() == d2.getFullYear();
    }

    public static compareDateTimes(d1: Date, d2: Date): boolean {
        return DateUtils.compareDates(d1, d2) && d1.getHours() == d2.getHours() && d1.getMinutes() == d2.getMinutes() && d1.getSeconds() == d2.getSeconds() && d1.getMilliseconds() == d2.getMilliseconds();
    }

    /**
     * Zkombinuje datum z prvniho parametru s casem z druheho parametru
     */
    public static unionDateAndTime(date: Date, time: Date): Date {
        /*
        let ms = DateUtils.miliSecPerDay;

        let d = <number>date; // v JS jde s Date pracovat jako s cislem ktere reprezentuje hodnotu Date.valueOf()
        let _t = d % ms;
        d = d - (_t < 0 ? _t + ms : _t);

        let t = <number>time;
        _t = t % ms;
        t = _t < 0 ? _t + ms : _t;

        return new Date(d + t);
     */
        const result = new Date(time.getTime());
        result.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        return result;
    }

    /**
     * Zkombinuje datum z prvniho parametru s casem z druheho parametru
     */
    public static localDateTimeToTime(datetime: Date | number): number {
        const ms = DateUtils.miliSecPerDay;

        let t = datetime as number; // v JS jde s Date pracovat jako s cislem ktere reprezentuje hodnotu Date.valueOf()
        const _t = t % ms;
        t = _t < 0 ? _t + ms : _t;

        return t;
    }

    /**
     * OADate (OLE Automation date) - vystup jako DateTime.ToOADate v .NET
     */
    public static toOaDate(date: Date): number {
        return (date as any as number - (new Date(Date.UTC(1899, 11, 30)) as any as number)) / DateUtils.miliSecPerDay;
    }

    /**
     * Nacteni formatovaci funkce
     * @param format Format
     * @param culture Nepovinny parametr culture, ktery urcuje, zda bude pro tento konkretni format pouzita jina kultura nez globalni
     */
    public static getDateToStringFunction(format: string, culture?: string): DateTimeFormatObject {
        if (!format)
            format = "g";

        const cn = culture ? culture : CultureInfo.getCultureInfo().name;
        // Zkontroluje, zda je zadana funkce seznamu, pokud je tak ji vrati, pokud neni projde celym ancablem tech ifu. Je to zde pro urychleni behu.
        if (!DateUtils._dateToStringFunctionList[format])
            DateUtils._dateToStringFunctionList[format] = {};
        if (DateUtils._dateToStringFunctionList[format][cn])
            return DateUtils._dateToStringFunctionList[format][cn];

        // podle http://msdn.microsoft.com/en-us/library/az4se3k1(v=vs.110).aspx

        if (format.toLowerCase() === "r") { // RFC-1123 format (ddd, dd MMM yyyy HH:mm:ss GMT) v UTC zone
            DateUtils._dateToStringFunctionList[format][cn] = {
                format: (date: Date) => {
                    return date.toUTCString();
                },
            };
        } else if (format.toLowerCase() === "i") { // ISO format (YYYY-MM-DDTHH:mm:ss.sssZ) v UTC zone
            DateUtils._dateToStringFunctionList[format][cn] = {
                format: (date: Date) => {
                    return date.toISOString();
                },
            };
        } else if (format === "s") { // sortable v lokalnim case bez ms
            DateUtils._dateToStringFunctionList[format][cn] = {
                format: (date: Date) => {
                    const d = new Date(date.getTime());
                    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                    return d.toISOString().slice(0, -5);
                },
            };
        } else if (format === "o") { // cas v lokalni zone v ISO formatu plus info o posunu vuci UTC ve forme +02:00
            DateUtils._dateToStringFunctionList[format][cn] = {
                format: (date: Date) => {
                    const d = new Date(date.getTime()),
                        off = date.getTimezoneOffset(),
                        pad = (s: string) => {
                            return s[1] ? s : "0" + s;
                        };
                    d.setMinutes(d.getMinutes() - off);
                    return d.toISOString().replace("Z", "") + (off > 0 ? "-" : "+") + pad(Math.floor(Math.abs(off / 60)) + "") + ":" + pad(Math.abs(off % 60) + "");
                },
            };
        } else if (format === "u") { // cas je v UTC
            DateUtils._dateToStringFunctionList[format][cn] = {
                format: (date: Date) => {
                    return date.toISOString().replace("T", " ");
                },
            };
        } else if (format === "dd") {
            DateUtils._dateToStringFunctionList[format][cn] = new Intl.DateTimeFormat(cn); // nativni fce
        } else if (format === "U") { // vysledek zobrazen v narodnim jazyce ale cas je v UTC
            if (CultureInfo.hasI18N) {
                DateUtils._dateToStringFunctionList[format][cn] = new Intl.DateTimeFormat(cn, CultureInfo.getStandardDateTimeOptions(format));
            } else {
                DateUtils._dateToStringFunctionList[format][cn] = {
                    format: (date: Date) => {
                        const d = new Date(date.getTime());
                        d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                        return DateUtils.dateToString(d, format);
                    },
                };
            }
        } else if (!CultureInfo.hasI18N || format.length > 1) { // Tesco fce
            DateUtils._dateToStringFunctionList[format][cn] = {
                format: (date: Date) => {
                    return DateUtils.dateToString(date, format);
                },
            };
        } else {
            const opts = CultureInfo.getStandardDateTimeOptions(format);
            DateUtils._dateToStringFunctionList[format][cn] = new Intl.DateTimeFormat(cn, opts); // nativni fce
        }

        return DateUtils._dateToStringFunctionList[format][cn];
    }

    public static dateToString(date: Date, format: string = "g"): string {
        function p(s: number, l: number = 2): string {
            return ("000" + s).slice(l * -1);
        }

        if (isNaN(date.getTime())) {
            return "";
        }

        if (format && format.length === 1)
            format = CultureInfo.getStandardDateTimeFormat(format);

        const x: any = date;
        const df = CultureInfo.getCultureInfo().dateTimeFormat;
        x.h = x.getHours;

        return format.replace(/(\\)?(dd?d?d?|MM?M?M?|yy?y?y?|hh?|HH?|mm?|ss?|tt?|f{0,7})/g,
            function(m: string): any {
                if (m.charAt(0) === "\\") {
                    return m.replace("\\", "");
                }

                switch (m) {
                    case "hh":
                    {
                        const h = x.h();
                        return p(h < 13 ? (h === 0 ? 12 : h) : (h - 12));
                    }
                    case "h":
                    {
                        const h = x.h();
                        return h < 13 ? (h === 0 ? 12 : h) : (h - 12);
                    }
                    case "HH":
                        return p(x.h());
                    case "H":
                        return x.h();
                    case "mm":
                        return p(x.getMinutes());
                    case "m":
                        return x.getMinutes();
                    case "ss":
                        return p(x.getSeconds());
                    case "s":
                        return x.getSeconds();
                    case "yyyy":
                        return p(x.getFullYear(), 4);
                    case "yy":
                        return p(x.getFullYear());
                    case "dddd":
                        return df.dayNames[x.getDay()];
                    case "ddd":
                        return df.abbreviatedDayNames[x.getDay()];
                    case "dd":
                        return p(x.getDate());
                    case "d":
                        return x.getDate();
                    case "MMMM":
                        return df.monthNames[x.getMonth()];
                    case "MMM":
                        return df.abbreviatedMonthNames[x.getMonth()];
                    case "MM":
                        return p((x.getMonth() + 1));
                    case "M":
                        return x.getMonth() + 1;
                    case "t":
                        return x.h() < 12 ? df.amDesignator.substring(0, 1) : df.pmDesignator.substring(0, 1);
                    case "tt":
                        return x.h() < 12 ? df.amDesignator : df.pmDesignator;
                    case "f":
                        return Math.floor(x.getMilliseconds() / 100);
                    case "ff":
                        return p(Math.floor(x.getMilliseconds() / 10));
                    case "fff":
                    case "ffff":
                    case "fffff":
                    case "ffffff":
                    case "fffffff":
                        return p(x.getMilliseconds(), 3);
                    default:
                        return m;
                }
            });
    }

    /* Ziska datum z uzivatelem psaneho textu, vraci jej jako objekt typu Date. V pripade nevalidniho vstupu vraci null. */
    public static getDateFromKbd(value: string): Date | null {
        if (!value)
            return null;

        const fmtInfo = this.getFormatInfo();
        let rexstr = "^\\s*";
        for (let i = 0; i < fmtInfo.formatOrder.length; i++) {
            if (fmtInfo.formatOrder[i] === "d")
                rexstr += "(\\d{1,2})" + (fmtInfo.daySeparator === "." ? "\\.\\s*" : fmtInfo.daySeparator);
            else if (fmtInfo.formatOrder[i] === "m")
                rexstr += "(\\d{1,2})" + (fmtInfo.monthSeparator === "." ? "\\.\\s*" : fmtInfo.monthSeparator);
            else
                rexstr += "(\\d{2}|\\d{4})" + (fmtInfo.yearSeparator === "." ? "\\.\\s*" : fmtInfo.yearSeparator);
        }
        rexstr += "\\s*$";

        const res = new RegExp(rexstr).exec(value);

        if (!res) // nevalidni format data
            return null;

        const y = fmtInfo.formatOrder.indexOf("y");
        const m = fmtInfo.formatOrder.indexOf("m");
        const d = fmtInfo.formatOrder.indexOf("d");
        const yr = res[y + 1].length === 2 ? "20" + res[y + 1] : res[y + 1]; // doplneni roku o 20

        const date = new Date(+yr, +res[m + 1] - 1, +res[d + 1]);
        if (date.getFullYear() === +yr && date.getMonth() === +res[m + 1] - 1 && date.getDate() === +res[d + 1])
            return date;
        else
            return null;
    }

    public static getFormatInfo(): dateFormatInfo {
        const fmt = CultureInfo.getStandardDateTimeFormat("d");
        if (!fmt)
            throw new Error("Invalidate date format for short format 'd'.");

        const groups = /(d+|m+|y+)([^dmy]+)(d+|m+|y+)([^dmy]+)(d+|m+|y+)([^dmy]*)/i.exec(fmt);
        let fmtOrder = "";
        let daySep = "";
        let monthSep = "";
        let yearSep = "";

        if (!groups)
            throw new Error("Invalidate date format for short format 'd'.");
        for (let i = 1; i < 6; i += 2) {
            if (/d/i.test(groups[i])) {
                fmtOrder += "d";
                daySep = groups[i + 1];
            } else if (/m/i.test(groups[i])) {
                fmtOrder += "m";
                monthSep = groups[i + 1];
            } else if (/y/i.test(groups[i])) {
                fmtOrder += "y";
                yearSep = groups[i + 1];
            }
        }

        return {
            daySeparator: daySep,
            monthSeparator: monthSep,
            yearSeparator: yearSep,
            formatOrder: fmtOrder,
        };
    }

    /**
     * Vraci specificke datum podle toho, jaky krok se zada. Pro odcitani zadat zapornou hodnotu prvniho parametru
     * @param step pocet dnu, o ktere se ma datum posunout dopredu/dozadu
     * @param separator oddelovac dnu, mesicu a roku
     */
    public static getSpecificDate(step: number, separator: string = "-"): string | null {
        if (separator == null) {
            return null;
        }
        const date = new Date();
        date.setDate(date.getDate() + step);

        const dd = date.getDate();
        const mm = date.getMonth() + 1;
        const yyyy = date.getFullYear();

        return dd + separator + mm + separator + yyyy;
    }
}

export interface DateTimeFormatObject {
    format: (date: Date) => string;
}

export interface dateFormatInfo {
    daySeparator: string;
    monthSeparator: string;
    yearSeparator: string;
    formatOrder: string;
}

GlobalContext.registerType(DateUtils);
